import React from 'react';
import { serviceDetails } from '../data/serviceDetails';
import OtherBCServices from '../components/OtherBCServices';

type ResultsProps = {
  answers: { [key: string]: string };
};

type Service = {
  id: string;
  name: string;
  organization: string;
  description: string;
  link: string;
};

type Program = {
  id: string;
  name: string;
  locations: string[];
  officialLink: string;
};

type ResultResponse = {
  program: Program;
  services: Service[];
  reasoning: string;
  urgentActions: string[];
  nextSteps: string[];
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const Results: React.FC<ResultsProps> = ({ answers }) => {
  const [result, setResult] = React.useState<ResultResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);
  const [submitError, setSubmitError] = React.useState('');
  const [comment, setComment] = React.useState('');
  const [expandedService, setExpandedService] = React.useState<string | null>(null);

  React.useEffect(() => {
    setLoading(true);
    setError('');
    fetch(`${API_URL}/recommend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(answers),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch results');
        return res.json();
      })
      .then((data: ResultResponse) => {
        setResult(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Error fetching results');
        setLoading(false);
      });
  }, [answers]);

  const handleRatingClick = (rating: string, userComment: string) => {
    setSubmitError('');
    const recommendationData = result ? {
      primaryOrg: result.program.name,
      servicesCount: result.services.length,
      services: result.services.map(s => s.name),
      reasoning: result.reasoning,
      hasUrgentActions: result.urgentActions?.length > 0,
      hasNextSteps: result.nextSteps?.length > 0,
    } : null;

    fetch(`${API_URL}/api/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        rating,
        comment: userComment,
        q1: answers.q1 || '',
        q2: answers.q2 || '',
        q3: answers.q3 || '',
        q4: answers.q4 || '',
        q5: answers.q5 || '',
        q6: answers.q6 || '',
      }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Failed to submit feedback');
        setSubmitted(true);
      })
      .catch((err) => setSubmitError('Failed to submit feedback. Please try again.'));
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-5">
        <div className="text-center py-16 text-gray-500 text-lg">
          Finding the best services for you...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto p-5">
        <div className="text-center p-10 bg-red-50 border border-red-200 rounded-xl">
          <h2 className="text-xl font-bold mb-2">⚠️ Something went wrong</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!result) {
    return <div className="max-w-3xl mx-auto p-5">No results found.</div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-5 font-sans text-gray-900 leading-relaxed">

      {/* Header */}
      <div className="text-center mb-10 pb-5 border-b-2 border-gray-200">
        <h1 className="text-3xl text-blue-600 font-bold mb-2">Your Personalized Recommendations</h1>
        <p className="text-sm text-gray-500 max-w-xl mx-auto">
          This tool helps you understand which organizations may support you.
          It does not replace any service provider and does not give legal or immigration advice.
        </p>
      </div>

      {/* Reasoning */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <h2 className="text-2xl font-semibold mb-4 text-gray-900">📍 Why These Services?</h2>
        <p className="text-gray-700 leading-relaxed">{result.reasoning}</p>
      </div>

      {/* Urgent Actions */}
      {result.urgentActions?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-900">🚨 Urgent: Do These First</h2>
          <ul className="pl-6 space-y-2">
            {result.urgentActions.map((action, i) => (
              <li key={i} className="text-base text-gray-800">{action}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Primary Organization */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <h2 className="text-2xl font-semibold mb-4 text-gray-900">🏢 Primary Organization for You</h2>
        <div className="bg-blue-50 p-5 rounded-lg border-2 border-blue-600">
          <h3 className="text-2xl text-blue-600 font-bold mb-2">{result.program.name}</h3>
          <p className="text-gray-600 mb-4">
            <strong>Locations:</strong> {result.program.locations.join(', ')}
          </p>
          <a
            href={result.program.officialLink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 font-medium hover:underline"
          >
            🌐 Visit Website →
          </a>
        </div>
      </div>

      {/* Services */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <h2 className="text-2xl font-semibold mb-2 text-gray-900">✅ Services You Need</h2>
        <p className="text-sm text-gray-500 mb-4">Click on each service to see complete details, locations, and maps</p>

        {result.services.map((service, index) => {
          const details = serviceDetails[service.id];
          const isExpanded = expandedService === service.id;

          return (
            <div key={index} className="border border-gray-200 rounded-lg mb-4 overflow-hidden">
              <div
                className="p-5 bg-gray-50 cursor-pointer flex justify-between items-center hover:bg-gray-100 transition-colors"
                onClick={() => setExpandedService(isExpanded ? null : service.id)}
              >
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{service.name}</h3>
                  <p className="text-sm text-gray-500">Provided by: {service.organization}</p>
                </div>
                <span className="text-gray-400 text-lg">{isExpanded ? '▼' : '▶'}</span>
              </div>

              {isExpanded && details && (
                <div className="p-6 bg-white">
                  <p className="text-gray-700 mb-5 pb-4 border-b border-gray-100">{service.description}</p>

                  {details.sections?.map((section: any, idx: number) => (
                    <div key={idx} className="mb-8 pb-5 border-b border-gray-100">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">{section.title}</h4>

                      <div className="mb-5">
                        <strong className="block text-gray-900 mb-2">📋 What You Need:</strong>
                        <ul className="pl-6 leading-loose">
                          {section.whatYouNeed.map((item: string, i: number) => <li key={i}>{item}</li>)}
                        </ul>
                      </div>

                      <div className="mb-5">
                        <strong className="block text-gray-900 mb-2">📝 How to Apply:</strong>
                        <ol className="pl-6 leading-loose">
                          {section.howToApply.map((step: string, i: number) => <li key={i}>{step}</li>)}
                        </ol>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-gray-50 rounded-lg mb-5 text-sm">
                        <div><strong>💰 Cost:</strong> {section.cost}</div>
                        <div><strong>⏰ Time:</strong> {section.timeNeeded}</div>
                        <div><strong>🕐 Hours:</strong> {section.hours}</div>
                      </div>

                      {section.locations?.map((loc: any, i: number) => (
                        <div key={i} className="flex justify-between items-start gap-4 p-4 bg-yellow-50 border border-yellow-300 rounded-lg mb-3">
                          <div className="text-sm leading-relaxed">
                            <strong>{loc.name}</strong><br />
                            {loc.address}<br />
                            {loc.phone && <>📞 {loc.phone}</>}
                          </div>
                          {loc.map && (
                            <a href={loc.map} target="_blank" rel="noopener noreferrer"
                              className="px-4 py-2 bg-blue-600 text-white text-sm rounded no-underline whitespace-nowrap hover:bg-blue-700 transition-colors">
                              🗺️ Open Map
                            </a>
                          )}
                        </div>
                      ))}

                      {section.importantNotes?.length > 0 && (
                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-300 mt-4">
                          <strong className="block text-gray-900 mb-2">⚠️ Important Notes:</strong>
                          <ul className="pl-5 leading-loose">
                            {section.importantNotes.map((note: string, i: number) => <li key={i}>{note}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}

                  {!details.sections && (
                    <>
                      {details.whatYouNeed && (
                        <div className="mb-5">
                          <strong className="block text-gray-900 mb-2">📋 What You Need:</strong>
                          <ul className="pl-6 leading-loose">
                            {details.whatYouNeed.map((item: string, i: number) => <li key={i}>{item}</li>)}
                          </ul>
                        </div>
                      )}
                      {details.howToApply && (
                        <div className="mb-5">
                          <strong className="block text-gray-900 mb-2">📝 How to Apply:</strong>
                          <ol className="pl-6 leading-loose">
                            {details.howToApply.map((step: string, i: number) => <li key={i}>{step}</li>)}
                          </ol>
                        </div>
                      )}
                      {(details.cost || details.timeNeeded || details.hours) && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-gray-50 rounded-lg mb-5 text-sm">
                          {details.cost && <div><strong>💰 Cost:</strong> {details.cost}</div>}
                          {details.timeNeeded && <div><strong>⏰ Time:</strong> {details.timeNeeded}</div>}
                          {details.hours && <div><strong>🕐 Hours:</strong> {details.hours}</div>}
                        </div>
                      )}
                      {details.locations?.map((loc: any, i: number) => (
                        <div key={i} className="flex flex-col sm:flex-row justify-between items-start gap-4 p-4 bg-yellow-50 border border-yellow-300 rounded-lg mb-3">
                          <div className="text-sm leading-relaxed">
                            <strong>{loc.name}</strong><br />
                            {loc.address}<br />
                            {loc.phone && <>📞 {loc.phone}</>}
                          </div>
                          {loc.map && (
                            <a href={loc.map} target="_blank" rel="noopener noreferrer"
                              className="w-full sm:w-auto text-center px-4 py-2 bg-blue-600 text-white text-sm rounded no-underline hover:bg-blue-700 transition-colors">
                              🗺️ Open Map
                            </a>
                          )}
                        </div>
                      ))}
                      {details.importantNotes?.length > 0 && (
                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-300 mt-4">
                          <strong className="block text-gray-900 mb-2">⚠️ Important Notes:</strong>
                          <ul className="pl-5 leading-loose">
                            {details.importantNotes.map((note: string, i: number) => <li key={i}>{note}</li>)}
                          </ul>
                        </div>
                      )}
                    </>
                  )}

                  <a
                    href={service.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium no-underline hover:bg-blue-700 transition-colors"
                  >
                    Visit Official Website →
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Next Steps */}
      {result.nextSteps?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-900">🎯 Your Action Plan</h2>
          <ol className="pl-6 space-y-3">
            {result.nextSteps.map((step, i) => (
              <li key={i} className="text-base leading-relaxed text-gray-800">{step}</li>
            ))}
          </ol>
        </div>
      )}

      {/* Feedback */}
      {!submitted ? (
        <div className="bg-blue-50 p-6 rounded-xl text-center mt-8">
          <h3 className="text-lg font-semibold mb-4 text-gray-900">Was this helpful?</h3>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 500))}
            placeholder="Any comments or suggestions? (optional, max 500 characters)"
            rows={3}
            maxLength={500}
            className="w-full px-3 py-2 mb-4 rounded-lg border border-slate-300 text-sm resize-y font-inherit text-slate-800 focus:outline-none focus:border-blue-500"
          />
          <p className="text-xs text-gray-400 text-right -mt-3 mb-3">{comment.length}/500</p>
          <div className="flex gap-3 justify-center flex-wrap">
            {[
              { rating: 'helpful', emoji: '👍', label: 'Yes, very helpful' },
              { rating: 'somewhat', emoji: '🤷', label: 'Somewhat helpful' },
              { rating: 'not_helpful', emoji: '👎', label: 'Not helpful' },
            ].map(({ rating, emoji, label }) => (
              <button
                key={rating}
                onClick={() => handleRatingClick(rating, comment)}
                className="w-[120px] h-[100px] bg-white border-2 border-blue-600 rounded-xl cursor-pointer flex flex-col items-center justify-center p-3 hover:bg-blue-50 transition-colors"
              >
                <span className="text-3xl mb-2">{emoji}</span>
                <span className="text-xs text-gray-700">{label}</span>
              </button>
            ))}
          </div>
          {submitError && <p className="text-red-600 mt-3">{submitError}</p>}
        </div>
      ) : (
        <div className="bg-green-100 p-4 rounded-xl text-center text-green-800 mt-8">
          ✅ Thank you for your feedback! We're constantly improving to serve you better.
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 p-5 bg-gray-50 rounded-xl text-sm text-gray-500 text-center">
        <p>
          <strong>Important:</strong> You may be eligible for services from multiple organizations.
          Final eligibility and enrollment decisions are made by each service provider.
          Contact them directly for the most up-to-date information.
        </p>
      </div>

      <OtherBCServices />
    </div>
  );
};

export default Results;
